import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Accordion, AccordionSummary, CircularProgress, FormControl } from '@mui/material'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '../../../components/actions/Button'
import StringInput from '../../../components/inputs/StringInput'
import useUser from '../../../contexts/hooks/useUser'
import { trpc } from '../../../modules/trpc'
import { cleanIBAN, formatIBAN } from '../../../utils/formatIBAN'

interface Props {}

const SaveAccountNumber: React.FC<Props> = () => {
	const utils = trpc.useContext()

	const { user, userSettings } = useUser()

	const { t } = useTranslation('pages_contractInformation')

	const [accountNumber, setAccountNumber] = useState(userSettings?.bankAccountNumber || '')
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [showConfirmation, setShowConfirmation] = useState(false)
	const [isSuccess, setIsSuccess] = useState(false)

	// Handle input change with formatting
	const handleAccountNumberChange = (value: string) => {
		const cleaned = cleanIBAN(value)
		setAccountNumber(cleaned)
	}

	const editSettings = trpc.user.userSettings.edit.useMutation({
		onSuccess: () => {
			utils.user.userSettings.invalidate()
			setIsSubmitting(false)
			setIsSuccess(true)
			// Show checkmark for 1.5 seconds before hiding confirmation
			setTimeout(() => {
				setIsSuccess(false)
				setShowConfirmation(false)
			}, 1500)
		},
		onError: () => {
			setIsSubmitting(false)
			setShowConfirmation(false)
			setIsSuccess(false)
		},
	})

	const handleSubmit = () => {
		if (!user?.id || !accountNumber.trim()) return
		setShowConfirmation(true)
	}

	const handleConfirm = () => {
		if (!user?.id || !accountNumber.trim()) return
		setIsSubmitting(true)
		editSettings.mutate({
			userId: user.id,
			data: { bankAccountNumber: accountNumber.trim() },
		})
	}

	const handleCancel = () => {
		setShowConfirmation(false)
		setIsSuccess(false)
	}

	return (
		<Accordion className='billing-information-accordion'>
			<AccordionSummary
				expandIcon={<ExpandMoreIcon />}
				aria-controls='billing-content'
				id='billing-header'
			>
				<div className='title'>{t('accountNumber.title')}</div>
			</AccordionSummary>
			<div className='flex flex-col gap-4 p-4 mb-4'>
				{t('accountNumber.placeholder')}
				<FormControl onSubmit={handleSubmit} className='flex flex-col gap-2'>
					<StringInput
						label={t('accountNumber.title')}
						value={formatIBAN(accountNumber)}
						onChange={handleAccountNumberChange}
						disabled={isSubmitting || showConfirmation}
					/>

					{!showConfirmation ? (
						<Button className='!m-0' onClick={handleSubmit}>
							{t('accountNumber.save')}
						</Button>
					) : (
						<>
							<Button className='!m-0' onClick={handleConfirm} disabled={isSubmitting || isSuccess}>
								{isSubmitting ? (
									<>
										<CircularProgress size={20} sx={{ color: 'white' }} />
										{t('accountNumber.saving')}
									</>
								) : isSuccess ? (
									<>
										<CheckCircleIcon sx={{ color: 'white' }} />
										{t('accountNumber.saved')}
									</>
								) : (
									t('accountNumber.confirm')
								)}
							</Button>
							{!isSuccess && (
								<Button
									className='!m-0'
									color='danger'
									onClick={handleCancel}
									disabled={isSubmitting}
								>
									{t('accountNumber.cancel')}
								</Button>
							)}
						</>
					)}
				</FormControl>
			</div>
		</Accordion>
	)
}

export default SaveAccountNumber
